// Feedback: who is on which version, permissions, and the latest tickets filed
// from the phone app (read-only; work happens in App Issues — spec D7).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MobileAppId, ReleaseEnv } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';
import { issuesApi } from '../../services/issuesApi';
import { mobileApi } from './mobileApi';
import type { MobileStats } from './mobileApi';
import { Card, Chip, PLATFORMS } from './ui';

function Adoption({ app, env }: { app: MobileAppId; env: ReleaseEnv }) {
  const [s, setS] = useState<Record<string, MobileStats | null> | null>(null);
  useEffect(() => {
    let off = false; setS(null);
    Promise.all(PLATFORMS.map((p) => mobileApi.stats(app, env, p.id).catch(() => null))).then((r) => { if (!off) setS({ ios: r[0], android: r[1] }); });
    return () => { off = true; };
  }, [app, env]);
  return (
    <Card title="Who is on which version" sub="Phones seen in the last 24 h">
      {!s ? <div className="relative h-32"><RubyLoader variant="section" /></div> : (
        <div className="grid gap-5 md:grid-cols-2">{PLATFORMS.map((p) => {
          const st = s[p.id];
          return (
            <div key={p.id}>
              <div className="mb-2 text-xs font-semibold text-stone-600">{p.label} · {st?.phones ?? 0} phones</div>
              {!st || st.phones === 0 ? <p className="text-sm text-stone-500">No phones yet.</p> : (
                <div className="flex flex-col gap-2">{st.byBundle.slice(0, 5).map((b) => (
                  <div key={b.bundleId} className="grid grid-cols-[120px_1fr_44px] items-center gap-2 text-xs">
                    <span className="truncate font-mono">{b.bundleId}</span>
                    <div className="h-3 overflow-hidden rounded bg-stone-100"><div className="h-full rounded bg-[#DC143C]" style={{ width: `${Math.round((b.phones / st.phones) * 100)}%` }} /></div>
                    <span className="text-right tabular-nums">{Math.round((b.phones / st.phones) * 100)}%</span>
                  </div>))}</div>
              )}
            </div>
          );
        })}</div>
      )}
    </Card>
  );
}

function AppTickets({ app }: { app: MobileAppId }) {
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => {
    let off = false; setItems(null);
    issuesApi.list({ limit: 50 } as any).then((r: any) => {
      const mine = (r.issues ?? []).filter((i: any) => typeof i.context?.mobileApp === 'string' && i.context.mobileApp.startsWith(`${app}-`)).slice(0, 5);
      if (!off) setItems(mine);
    }).catch(() => { if (!off) setItems([]); });
    return () => { off = true; };
  }, [app]);
  return (
    <Card title="Latest from the app" sub="From App Issues" right={<Link to="/app-issues" className="inline-flex min-h-[44px] items-center rounded-xl border border-stone-200 px-4 text-sm font-semibold">Open App Issues</Link>}>
      {!items ? <div className="relative h-24"><RubyLoader variant="section" /></div>
        : items.length === 0 ? <p className="text-sm text-stone-500">No tickets from the app yet.</p>
        : <ul className="divide-y divide-stone-100">{items.map((i) => (
          <li key={i.id}><Link to={`/app-issues/${i.id}`} className="flex min-h-[52px] items-center justify-between gap-3 py-2 hover:text-[#9F1239]">
            <span><b className="text-sm">{i.title}</b><span className="block text-xs text-stone-500">{i.reporterName} · {i.context?.mobileApp} · {i.context?.bundleVersion ?? 'builtin'}</span></span>
            <Chip tone={i.status === 'new' ? 'warn' : i.status === 'resolved' ? 'ok' : 'info'}>{i.status.replace('_', ' ')}</Chip></Link></li>))}</ul>}
    </Card>
  );
}

export function FeedbackTab({ app, env }: { app: MobileAppId; env: ReleaseEnv }) {
  return <div className="flex flex-col gap-5"><Adoption app={app} env={env} /><AppTickets app={app} /></div>;
}
